import React from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import TimeAgo from '@components/TimeAgo';
import { ArticleClusterType } from '@components/pages/PageTemplates/interfaces';
import useEventTracking from '@hooks/useEventTracking';
import { article_path, project_path } from '@routes';
import { ClickTarget, ContentType, click } from '@tracking';

interface ItemProps {
  bylines: string;
  id: string;
  image: string;
  publishedAt: string;
  showByline: boolean;
  showImage: boolean;
  showTimestamp: boolean;
  slug: string;
  title: string;
  type: ArticleClusterType;
}

const Item = ({
  bylines,
  id,
  image,
  publishedAt,
  showByline,
  showImage,
  showTimestamp,
  slug,
  title,
  type,
}: ItemProps) => {
  const { trackEvent } = useEventTracking({
    contentType: type === ArticleClusterType.Article ? ContentType.Article : ContentType.Project,
  });

  const handleItemClick = () => {
    trackEvent(
      click({ clickTarget: ClickTarget.ArticleImage, contentId: id as unknown as number })
    );
  };

  const handleItemTitleClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.Headline,
        clickText: title,
        contentId: id as unknown as number,
      })
    );
  };

  const itemPath = type === ArticleClusterType.Article ? article_path(slug) : project_path(slug);

  return (
    <div className="flex flex-col pb-40 last:pb-0 sm:w-[calc(50vw-80px)] sm:pb-0 lg:w-[257px]">
      {showImage && image && (
        <a
          href={itemPath}
          className="relative mb-8 block h-[calc((100vw-2rem)*9/16)] sm:mb-0 sm:h-[calc((50vw-80px)*9/16)] lg:h-[calc(257px*9/16)]"
          onClickCapture={handleItemClick}
        >
          <LazyImgixImage
            className="m-auto block aspect-[16/9] h-auto w-full"
            src={image}
            sizes="(min-width: 768px) 250px, (min-width: 480px) 500px, 400px"
            wrapperClassName="h-full w-full"
          />
        </a>
      )}
      <div className="mt-8 sm:mt-12">
        <h2 className="m-0 font-suisse-works text-headline-xs text-black lg:text-headline-xxs">
          <a href={itemPath} className="text-black" onClickCapture={handleItemTitleClick}>
            {title}
          </a>
        </h2>
        {showTimestamp && publishedAt && (
          <TimeAgo className="inline-block text-grayscale-gray" date={publishedAt} />
        )}
        {showByline && bylines?.length > 0 && (
          <p className="mb-0 mt-2 !font-suisse-screen !text-byline-sm text-grayscale-gray">
            By {bylines}
          </p>
        )}
      </div>
    </div>
  );
};

export default Item;
